// License, workspace and legal entity stamping for new records (plan 4.2) and
// workspace scoped list queries (4.3). AuthContext sets the active context after the
// profile loads; fs.js stamps owner-scoped creates with it.
//
// Workspace scope is off until `config/app.workspaceScope` is true. Turn on the
// `workspaceEnforced()` rule flag first, then this; roll back in reverse order,
// otherwise workspace queries are rejected by the owner-only rules.

import { where } from "./fs";
import { filterByScope, normalizeScope } from "./orgScope";

export const DEFAULT_WORKSPACE_PREFIX = "default_";

// Must match ownerScoped() in firestore.rules, plus files.
export const WORKSPACE_SCOPED = new Set([
  "folders", "chatSessions", "chatMessages", "entityDocuments", "statusHistory",
  "invoices", "accounts", "transactions",
  "products", "stockMovements", "stockTransfers", "serialNumbers", "lots",
  "warehouses", "warehouseLocations", "goodsReceipts", "shipmentDocs", "purchaseRequests",
  "stockCounts", "inventoryPolicies",
  "suppliers", "rfqs", "supplierQuotations", "purchaseOrders", "boms", "engineeringRevisions",
  "productionPlans", "manufacturingOrders", "workOrders",
  "qualityPlans", "inspections", "nonConformances", "correctiveActions",
  "packages", "pallets", "finishedGoods",
  "freightForwarders", "exportOrders", "exportDocuments", "internationalShipments",
  "containers", "customsDeclarations", "overseasWarehouses",
  "customers", "priceLists", "salesOrders", "stockReservations", "pickLists", "pickWaves", "salesForecasts",
  "salesPacks", "dispatches", "deliveries",
  "salesReturns", "rmas", "warranties", "warrantyClaims", "serviceOrders",
  "notificationEvents", "financeLinks", "retentionPolicies", "disposalRequests", "disposalCertificates", "traceEvents", "traceLinks",
  "files"
]);

let workspaceScope = false;

// Must match auditorCollection() in firestore.rules, plus files.
export const AUDITOR_READ_COLLECTIONS = new Set([
  "invoices", "accounts", "transactions", "financeLinks",
  "folders", "entityDocuments", "statusHistory", "files"
]);

// Auditor role (2.6): lists read the parent company's records instead of the user's own.
let readOwner = null;

export function setReadOwner(uid) {
  readOwner = uid || null;
}

export function getReadOwner() {
  return readOwner;
}

// Collections whose rules cap the field list or that are not business data.
const NOT_STAMPED = new Set([
  "sessions", "loginLogs", "licenseLookupLogs", "supportRequests", "supportTickets", "messages",
  "exchangeRates", "mail", "privacyRequests",
  "legalEntities", "branches", "userScopes", "groupUsage"
]);

const MANAGER_ROLES = new Set(["admin", "company", "dealer", "management"]);

let context = { licenseCode: null, workspaceId: null, legalEntityId: null, tenantId: null };

// Legal entity / branch / warehouse scope of the signed-in user (4.3); null = unrestricted.
let scope = null;

export function setUserScope(raw) {
  scope = normalizeScope(raw);
}

export function getUserScope() {
  return scope;
}

/** Drops rows outside the user's scope; rules enforce the same once `scopeEnforced()` is on. */
export function applyUserScope(collectionName, rows) {
  return filterByScope(scope, rows, collectionName);
}

/** The account whose company the user belongs to (managers own their tenant). */
export function tenantOf(profile, uid) {
  if (!uid) return null;
  return MANAGER_ROLES.has(profile?.role) ? uid : profile?.parentUid || uid;
}

export function defaultWorkspaceId(tenantUid) {
  return tenantUid ? `${DEFAULT_WORKSPACE_PREFIX}${tenantUid}` : null;
}

export function licenseCodeOf(profile) {
  const code = profile?.licenseCode || (Array.isArray(profile?.licenseCodes) ? profile.licenseCodes[0] : null);
  return code ? String(code).replace(/-/g, "").toUpperCase() : null;
}

export function setTenancy(next) {
  context = {
    licenseCode: next?.licenseCode || null,
    workspaceId: next?.workspaceId || null,
    legalEntityId: next?.legalEntityId || null,
    tenantId: next?.tenantId || null
  };
}

export function getTenancy() {
  return context;
}

export function isStamped(collectionName) {
  return !!collectionName && !NOT_STAMPED.has(collectionName);
}

/** Adds licenseCode / workspaceId / legalEntityId when known and not already set by the caller. */
export function stampTenancy(collectionName, data) {
  if (!data || typeof data !== "object" || !isStamped(collectionName)) return data;
  const { licenseCode, workspaceId, legalEntityId } = context;
  if (!licenseCode && !workspaceId && !legalEntityId) return data;
  const out = { ...data };
  if (licenseCode && out.licenseCode == null) out.licenseCode = licenseCode;
  if (workspaceId && out.workspaceId == null) out.workspaceId = workspaceId;
  if (legalEntityId && out.legalEntityId == null) out.legalEntityId = legalEntityId;
  return out;
}

export function setWorkspaceScope(enabled) {
  workspaceScope = !!enabled;
}

export function isWorkspaceScopeOn() {
  return workspaceScope;
}

/**
 * The `[field, value]` a list query filters on: the active workspace when the
 * scope is on and the query is for the signed-in user, otherwise the owner.
 */
export function scopeFilter(collectionName, ownerFieldName, uid, signedInUid = uid) {
  const { workspaceId } = context;
  if (readOwner && uid && uid === signedInUid && AUDITOR_READ_COLLECTIONS.has(collectionName)) {
    return [ownerFieldName, readOwner];
  }
  if (workspaceScope && workspaceId && uid && uid === signedInUid && WORKSPACE_SCOPED.has(collectionName)) {
    return ["workspaceId", workspaceId];
  }
  return [ownerFieldName, uid];
}

export function scopeWhere(collectionName, ownerFieldName, uid, signedInUid = uid) {
  const [field, value] = scopeFilter(collectionName, ownerFieldName, uid, signedInUid);
  return where(field, "==", value);
}
